/**
 * Formats a date string (yyyy-MM-dd) or Date object to dd-MMM-yyyy
 * e.g. "2026-04-29" → "29-Apr-2026"
 */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value + 'T00:00:00') : value;
  if (isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

/**
 * Formats a time string (HH:mm or HH:mm:ss from backend LocalTime) to 12-hour
 * format with AM/PM — e.g. "14:00:00" → "2:00 PM", "09:30" → "9:30 AM"
 */
export function formatTime(value: string | null | undefined): string {
  if (!value) return '';
  const [hhStr, mmStr] = value.split(':');
  const hh = parseInt(hhStr, 10);
  const mm = parseInt(mmStr ?? '0', 10);
  if (isNaN(hh) || isNaN(mm)) return value;
  const period = hh >= 12 ? 'PM' : 'AM';
  const hour12 = hh % 12 === 0 ? 12 : hh % 12;
  const minStr = String(mm).padStart(2, '0');
  return `${hour12}:${minStr} ${period}`;
}

/**
 * Converts a backend LocalTime string ("HH:mm:ss") to "HH:mm" for use in
 * <input type="time"> value — e.g. "14:00:00" → "14:00"
 */
export function toTimeInputValue(value: string | null | undefined): string {
  if (!value) return '';
  return value.slice(0, 5); // "HH:mm"
}

/**
 * Formats an ISO datetime string to dd-MMM-yyyy HH:mm
 * e.g. "2026-04-29T14:30:00Z" → "29-Apr-2026 14:30"
 */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hh}:${mm}`;
}
